import { TFunction } from "i18next";

import { Status } from "src/core/constants/folders";
import { CreateClientEIFormValues } from "src/core/forms/create-client/createClientEI.schema";
import { CreateClientSirenFormValues } from "src/core/forms/create-client/createClientSiren.schema";
import { DataType, FolderFile, InputValueInput } from "src/core/gql/graphql";
import { FolderEntrepriseFormValues } from "src/core/types/folder";

/**
 * Construit le tableau des champs pour la mutation updateCorporation
 * à partir des valeurs du formulaire
 */
export const buildCorporationInputValues = (data: FolderEntrepriseFormValues) => {
  const baseFields: InputValueInput[] = [
    { fieldCode: "name", value: data.raisonSociale, dataType: DataType.String },
    {
      fieldCode: "registrationCountryCode",
      value: data.paysImmatriculation,
      dataType: DataType.Code,
    },
    { fieldCode: "activityCode", value: data.activite, dataType: DataType.Code },
    { fieldCode: "registration", value: data.siren, dataType: DataType.String },
    { fieldCode: "entityTypeCode", value: data.typeJuridique, dataType: DataType.Code },
    { fieldCode: "customListCode2", value: data.formeJuridique, dataType: DataType.Code },
    {
      fieldCode: "customString3",
      value: data.formeJuridiquePrecision || "",
      dataType: DataType.String,
    },
    { fieldCode: "customString1", value: data.codePostal, dataType: DataType.String },
    { fieldCode: "customString2", value: data.adresse, dataType: DataType.String },
    { fieldCode: "customCountryCode1", value: data.pays, dataType: DataType.Code },
  ];

  return baseFields;
};

export const buildCreateClientByRaisonSocialeInputValues = (
  raisonSociale: string,
  siren: string | null
) => {
  const baseFields: InputValueInput[] = [
    { fieldCode: "name", value: raisonSociale, dataType: DataType.String },
    { fieldCode: "entityTypeCode", value: "unlisted", dataType: DataType.Code },
  ];
  if (siren) {
    baseFields.push({ fieldCode: "registration", value: siren, dataType: DataType.String });
  }
  return baseFields;
};

export const buildCreateClientPPHInputValues = (
  data: CreateClientEIFormValues | CreateClientSirenFormValues,
  t: TFunction
) => {
  const baseFields: InputValueInput[] = [
    {
      fieldCode: "firstName",
      value: "prenomEI" in data ? data.prenomEI : t("form.createClient.emptyPPHName"),
      dataType: DataType.String,
    },
    {
      fieldCode: "lastName",
      value: "nomEI" in data ? data.nomEI : t("form.createClient.emptyPPHName"),
      dataType: DataType.String,
    },
  ];
  if ("dateDeNaissanceEI" in data) {
    baseFields.push({
      fieldCode: "birthDate",
      dataType: DataType.Timestamp,
      value: new Date("dateDeNaissanceEI" in data ? data.dateDeNaissanceEI : "")
        .toISOString()
        .replace(/\+\d{2}:\d{2}/, "Z"),
    });
  }
  if ("siren" in data) {
    baseFields.push({ fieldCode: "registration", value: data.siren, dataType: DataType.String });
  }

  return baseFields;
};

export const dateFolderFinished = (folderFile: FolderFile): string | null => {
  const folderState = folderFile?.workflowStatus?.state?.code;
  const isFolderFinished =
    (folderState === Status.VALIDATED || folderState === Status.REJECTED) &&
    !folderFile.currentCustomerFolder?.missions.some((mission) => mission.isClosable);

  if (isFolderFinished) {
    const missions = folderFile.currentCustomerFolder?.missions || [];
    const lastModificationDates = missions
      .map((mission) => mission.adminStatus?.lastModification)
      .filter((date): date is string => date !== null && date !== undefined);

    if (lastModificationDates.length === 0) {
      return null;
    }

    return lastModificationDates.reduce((latest, current) => {
      return new Date(current) > new Date(latest) ? current : latest;
    });
  }

  return null;
};
